import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Mail, KeyRound, ShieldCheck, User as UserIcon, Edit, Save } from 'lucide-react';
import { toast } from 'sonner';

export default function Settings() {
  const { user, authAxios, updateUser, token } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Self-service form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newEmail, setNewEmail] = useState(user?.email || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingSelf, setSavingSelf] = useState(false);

  // Admin-only user management
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ new_email: '', new_password: '', new_role: '', new_full_name: '' });
  const [savingUser, setSavingUser] = useState(false);

  useEffect(() => {
    setNewEmail(user?.email || '');
  }, [user?.email]);

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const res = await authAxios.get('/auth/users');
      setUsers(res.data);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (isAdmin) fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const handleSelfUpdate = async (e) => {
    e.preventDefault();
    if (!currentPassword) {
      toast.error('Current password is required to make changes');
      return;
    }
    const wantsEmailChange = newEmail && newEmail !== user.email;
    const wantsPasswordChange = !!newPassword;
    if (!wantsEmailChange && !wantsPasswordChange) {
      toast.error('Change your email or set a new password');
      return;
    }
    if (wantsPasswordChange && newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (wantsPasswordChange && newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    setSavingSelf(true);
    try {
      const payload = { current_password: currentPassword };
      if (wantsEmailChange) payload.new_email = newEmail;
      if (wantsPasswordChange) payload.new_password = newPassword;
      const res = await authAxios.put('/auth/update-credentials', payload);
      if (res.data.token) {
        localStorage.setItem('token', res.data.token);
      }
      updateUser(res.data.user);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Your credentials have been updated');
      // Force a refresh of the page to use the new token
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update credentials');
    } finally {
      setSavingSelf(false);
    }
  };

  const openEditUser = (u) => {
    setEditingUser(u);
    setEditForm({
      new_email: u.email,
      new_password: '',
      new_role: u.role,
      new_full_name: u.full_name || '',
    });
  };

  const handleAdminUpdate = async () => {
    if (!editingUser) return;
    const payload = {};
    if (editForm.new_email && editForm.new_email !== editingUser.email) payload.new_email = editForm.new_email;
    if (editForm.new_password) {
      if (editForm.new_password.length < 8) {
        toast.error('Password must be at least 8 characters');
        return;
      }
      payload.new_password = editForm.new_password;
    }
    if (editForm.new_role && editForm.new_role !== editingUser.role) payload.new_role = editForm.new_role;
    if (editForm.new_full_name && editForm.new_full_name !== editingUser.full_name) payload.new_full_name = editForm.new_full_name;

    if (Object.keys(payload).length === 0) {
      toast.error('Nothing to update');
      return;
    }
    setSavingUser(true);
    try {
      const res = await authAxios.put(`/auth/users/${editingUser.id}/credentials`, payload);
      toast.success('User updated successfully');
      setUsers((prev) => prev.map((u) => (u.id === editingUser.id ? res.data.user : u)));
      // If admin updated themselves, refresh self user too
      if (editingUser.id === user.id) {
        updateUser(res.data.user);
      }
      setEditingUser(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    } finally {
      setSavingUser(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="settings-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-stone-900 font-heading">Settings</h1>
        <p className="text-stone-600 mt-1">Manage your login credentials{isAdmin ? ' and team accounts' : ''}.</p>
      </div>

      <Tabs defaultValue="account" className="space-y-6">
        <TabsList>
          <TabsTrigger value="account" data-testid="settings-tab-account">
            <UserIcon className="w-4 h-4 mr-2" /> My Account
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="users" data-testid="settings-tab-users">
              <ShieldCheck className="w-4 h-4 mr-2" /> Users & Roles
            </TabsTrigger>
          )}
        </TabsList>

        {/* Self-service tab */}
        <TabsContent value="account">
          <Card>
            <CardHeader>
              <CardTitle>Login Credentials</CardTitle>
              <CardDescription>
                Change your email (login ID) and/or password. You will be required to log in again with the new credentials.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSelfUpdate} className="space-y-5 max-w-xl">
                <div>
                  <Label htmlFor="current_email">Current Email (login ID)</Label>
                  <Input id="current_email" value={user?.email || ''} disabled className="bg-stone-50" />
                </div>
                <div>
                  <Label htmlFor="new_email">New Email</Label>
                  <Input
                    id="new_email"
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="you@company.com"
                    data-testid="settings-new-email"
                  />
                </div>
                <div>
                  <Label htmlFor="new_password">New Password (optional)</Label>
                  <Input
                    id="new_password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Leave blank to keep current password"
                    data-testid="settings-new-password"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm_password">Confirm New Password</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repeat the new password"
                    data-testid="settings-confirm-password"
                  />
                </div>
                <div className="border-t pt-5">
                  <Label htmlFor="current_password" className="text-stone-900">
                    Current Password (required to confirm)
                  </Label>
                  <Input
                    id="current_password"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter your current password"
                    required
                    data-testid="settings-current-password"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={savingSelf}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="settings-save-self-btn"
                >
                  {savingSelf ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" /> Update Credentials</>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Admin user management tab */}
        {isAdmin && (
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>All Users</CardTitle>
                <CardDescription>
                  Edit any user's login email, password, role (Admin / Employee), and full name. There can be multiple admins.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingUsers ? (
                  <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-stone-600">
                          <th className="py-3 pr-4">Name</th>
                          <th className="py-3 pr-4">Login Email</th>
                          <th className="py-3 pr-4">Role</th>
                          <th className="py-3 pr-4">Status</th>
                          <th className="py-3 pr-4 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((u) => (
                          <tr key={u.id} className="border-b hover:bg-stone-50" data-testid={`settings-user-row-${u.id}`}>
                            <td className="py-3 pr-4 font-medium text-stone-900">{u.full_name}</td>
                            <td className="py-3 pr-4 text-stone-700">
                              <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{u.email}</span>
                            </td>
                            <td className="py-3 pr-4">
                              <Badge className={u.role === 'admin' ? 'bg-emerald-600' : 'bg-stone-500'}>
                                {u.role === 'admin' ? 'Admin' : 'Employee'}
                              </Badge>
                            </td>
                            <td className="py-3 pr-4">
                              <Badge variant="outline" className={u.status === 'pending_approval' ? 'text-amber-700 border-amber-300' : 'text-emerald-700 border-emerald-300'}>
                                {u.status || 'active'}
                              </Badge>
                            </td>
                            <td className="py-3 pr-4 text-right">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openEditUser(u)}
                                data-testid={`settings-edit-user-btn-${u.id}`}
                              >
                                <Edit className="w-3.5 h-3.5 mr-1.5" /> Edit
                              </Button>
                            </td>
                          </tr>
                        ))}
                        {users.length === 0 && (
                          <tr><td colSpan={5} className="py-8 text-center text-stone-500">No users found.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Admin edit user dialog */}
      <Dialog open={!!editingUser} onOpenChange={(open) => !open && setEditingUser(null)}>
        <DialogContent className="max-w-md" data-testid="settings-edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update credentials and role for {editingUser?.full_name}. Leave password blank to keep it unchanged.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Full Name</Label>
              <Input
                value={editForm.new_full_name}
                onChange={(e) => setEditForm((f) => ({ ...f, new_full_name: e.target.value }))}
                data-testid="settings-edit-full-name"
              />
            </div>
            <div>
              <Label>Login Email</Label>
              <Input
                type="email"
                value={editForm.new_email}
                onChange={(e) => setEditForm((f) => ({ ...f, new_email: e.target.value }))}
                data-testid="settings-edit-email"
              />
            </div>
            <div>
              <Label>New Password (optional)</Label>
              <Input
                type="password"
                placeholder="Leave blank to keep unchanged"
                value={editForm.new_password}
                onChange={(e) => setEditForm((f) => ({ ...f, new_password: e.target.value }))}
                data-testid="settings-edit-password"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select
                value={editForm.new_role}
                onValueChange={(v) => setEditForm((f) => ({ ...f, new_role: v }))}
              >
                <SelectTrigger data-testid="settings-edit-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="employee">Employee</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUser(null)} disabled={savingUser}>Cancel</Button>
            <Button
              onClick={handleAdminUpdate}
              disabled={savingUser}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="settings-save-user-btn"
            >
              {savingUser ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving</> : <><KeyRound className="w-4 h-4 mr-2" />Save Changes</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
